/**
 * Single source of truth for recognising the api's billing-gate 403.
 *
 * The gate returns AUTHORIZATION_ERROR with a machine-readable `details.reason`
 * (see _block_details in api/app/dependencies/organization_validation.py). Keying
 * off that — rather than any 403 — avoids mistaking a membership/permission denial
 * for a billing block, and survives changes to the English message.
 */

/** Reason codes the gate emits (alignment.evaluate_alignment). */
export const BILLING_BLOCK_REASONS = [
  'trial_exceeded',
  'tier_cap_exceeded',
  'subscription_past_due',
  'subscription_inactive',
  'subscription_expired',
] as const

/**
 * Legacy fallback for an api that predates `details.reason`. Remove once every
 * deployed api emits structured details.
 */
const LEGACY_BLOCK_PHRASES = [
  'subscription has expired',
  'subscription is invalid',
  'subscription is inactive',
  'subscription payment is past due',
  'free trial limit',
  'record limit has been reached',
]

export const BILLING_BLOCKED_EVENT = 'glaium:billing-blocked'

export interface BillingBlockDetails {
  reason?: string
  organization_id?: number
  total_records?: number
  committed_tier?: string
  tier_cap?: number | null
  required_tier?: string
  required_tier_price?: number
  is_free?: boolean
  upgrade_url?: string | null
}

export function billingBlockDetails(error: any): BillingBlockDetails | null {
  if (error?.response?.status !== 403) return null
  const body = error?.response?.data
  if (body?.error !== 'AUTHORIZATION_ERROR') return null

  const details: BillingBlockDetails | undefined = body.details
  if (details?.reason && (BILLING_BLOCK_REASONS as readonly string[]).includes(details.reason)) {
    return details
  }

  const message: string = body.message || ''
  if (LEGACY_BLOCK_PHRASES.some((p) => message.includes(p))) return {}

  return null
}

export function billingBlockMessage(error: any): string | null {
  return billingBlockDetails(error) === null ? null : error?.response?.data?.message || ''
}

export function isBillingBlockError(error: any): boolean {
  return billingBlockDetails(error) !== null
}
